import { DialogRef } from '../classes/dialog.ref';
import { DIALOG_DATA } from '../tokens/dialog.token';
import { DialogDeleteComponent } from '@aca-new/app/shared/components/modal/shared/components/dialog-delete/dialog-delete.component';
import { IDialogDelete } from '@aca-new/app/shared/components/modal/shared/components/dialog-delete/shared/interfaces/dialog-delete.interface';
import { EPositionStrategy } from '@aca-new/app/shared/components/modal/shared/models/enums/position-strategy.enum';
import { ComponentType, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector, StaticProvider } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { QimaOptionalType } from '@qima/ngx-qima';
import { Observable } from 'rxjs';

export interface IDialogConfig {
  backdropClass?: string | string[];
  data?: unknown;
  hasBackdrop?: boolean;
  panelClass?: string | string[];
  positionStrategy?: EPositionStrategy;
  stopCloseWhenClickBackdrop?: boolean;
}

@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class DialogService {
  public constructor(
    private readonly _overlay: Overlay,
    private readonly _injector: Injector
  ) {}

  public open<T1, T2>(component: ComponentType<T1>, config?: IDialogConfig, providers?: StaticProvider[]): DialogRef<T2> {
    // Globally centered position strategy
    const positionStrategy = this._overlay.position().global()[config?.positionStrategy === EPositionStrategy.BOTTOM ? 'bottom' : 'centerVertically']('0').centerHorizontally();
    // Create the overlay with customizable options
    const basePanelClass: string = config?.positionStrategy === EPositionStrategy.BOTTOM ? 'overlay-panel-bottom' : 'overlay-panel';
    const extraPanelClasses: string[] = config?.panelClass ? (Array.isArray(config.panelClass) ? config.panelClass : [config.panelClass]) : [];
    const backdropClasses: string[] = config?.backdropClass ? (Array.isArray(config.backdropClass) ? config.backdropClass : [config.backdropClass]) : ['overlay-backdrop'];
    const overlayRef = this._overlay.create({
      positionStrategy,
      hasBackdrop: config?.hasBackdrop ?? true,
      backdropClass: backdropClasses,
      panelClass: [basePanelClass, ...extraPanelClasses].filter(Boolean),
      disposeOnNavigation: true,
    });
    // Create dialogRef to return
    const dialogRef = new DialogRef<T2>(overlayRef);

    // close when click backend
    overlayRef
      .backdropClick()
      .pipe(untilDestroyed(this))
      .subscribe((): void => {
        if (config?.stopCloseWhenClickBackdrop) {
          return;
        }

        dialogRef.close();
      });
    // Create injector to be able to reference the DialogRef from within the component
    const injector = Injector.create({
      parent: this._injector,
      providers: [{ provide: DialogRef, useValue: dialogRef }, { provide: DIALOG_DATA, useValue: config?.data }, ...(providers ?? [])],
    });
    // Attach component portal to the overlay
    const portal = new ComponentPortal(component, null, injector);

    overlayRef.attach(portal);
    document.body.style.overflow = 'hidden';

    return dialogRef;
  }

  public confirmDelete$(deleteData: IDialogDelete): Observable<QimaOptionalType<string>> {
    const dialogRef: DialogRef<string> = this.open(DialogDeleteComponent, {
      data: deleteData,
    });

    return dialogRef.afterClosed$();
  }
}
